import React, { useState, useRef, useLayoutEffect } from 'react';
import { fetchServiceEntries, addServiceEntry, updateServiceEntry, deleteServiceEntry, ServiceEntry } from '../lib/serviceBookApi';
import ServiceBookForm, { ServiceBookSubmitData } from './ServiceBookForm';
import SwipeToDelete from 'react-swipe-to-delete-ios';
import { getCurrentUser } from '../lib/supabase';
import { Wrench, Fuel, BadgeCheck, HelpCircle, Pencil, Trash2 } from 'lucide-react';
import { formatEntryDate } from '../lib/dateUtils';
import { formatNumberSR, formatCurrencySR } from '../lib/formatUtils';

const SERVICE_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'repair', label: 'Repair' },
  { value: 'oil change', label: 'Oil Change' },
  { value: 'registration', label: 'Registration' },
  { value: 'tire change', label: 'Tire Change' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'battery', label: 'Battery' },
  { value: 'brake', label: 'Brake Service' },
  { value: 'air conditioning', label: 'Air Conditioning' },
  { value: 'lights', label: 'Lights' },
  { value: 'other', label: 'Other' },
];

const TYPE_ICONS: Record<string, React.ElementType> = {
  'repair': Wrench,
  'oil change': Fuel,
  'registration': BadgeCheck,
};

function ServiceBookEntry({ entry, Icon, onEdit, onDelete }: {
  entry: ServiceEntry;
  Icon: React.ElementType;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState<number>(56);
  useLayoutEffect(() => {
    if (cardRef.current) {
      const h = cardRef.current.offsetHeight;
      if (h && h !== cardHeight) setCardHeight(h);
    }
    // eslint-disable-next-line
  }, [entry.notes, entry.mileage, entry.type, entry.price, entry.date]);

  const handleDeleteWithConfirm = () => {
    onDelete();
  };

  return (
    <div className="mb-3">
      <SwipeToDelete
        onDelete={handleDeleteWithConfirm}
        deleteColor="transparent"
        deleteComponent={
          <div className="flex h-full items-center justify-end pr-2">
            <button
              className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white font-bold px-3 py-1 rounded shadow transition-colors min-h-0 min-w-[70px]"
              style={{ fontSize: 14 }}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        }
        height={cardHeight}
        transitionDuration={250}
        rtl={false}
      >
        <div ref={cardRef} className="w-full flex items-center gap-2 bg-theme-primary rounded-lg shadow-sm border border-theme-primary p-2 min-h-14 relative overflow-hidden">
          <div className="flex-shrink-0 w-8 h-8 rounded-md bg-primary-600 flex items-center justify-center">
            <Icon className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0 flex flex-row justify-between items-start">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-theme-primary leading-tight block truncate">{entry.notes || '—'}</span>
                <button
                  onClick={e => { e.stopPropagation(); onEdit(); }}
                  className="p-1 rounded-md text-theme-tertiary hover:text-primary-600 transition-colors z-10"
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                <span className="text-theme-secondary text-xs">{formatNumberSR(entry.mileage, { maximumFractionDigits: 0 })} km</span>
              </div>
              <div className="text-theme-tertiary text-xs mt-1 leading-tight capitalize">{entry.type}</div>
            </div>
            <div className="flex flex-col items-end ml-2 min-w-[70px]">
              <span className="text-xs text-theme-secondary leading-tight">{formatEntryDate(entry.date)}</span>
              <span className="text-primary-600 font-bold text-sm mt-1">{formatCurrencySR(entry.price)}</span>
            </div>
          </div>
        </div>
      </SwipeToDelete>
    </div>
  );
}

const ServiceBookSection: React.FC = () => {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState<ServiceEntry | null>(null);
  const [entries, setEntries] = useState<ServiceEntry[]>([]);
  const [loading, setLoading] = useState(false);
  // Ukloni openActionId state

  // Fetch entries on mount and when search/type changes
  const refreshEntries = React.useCallback(() => {
    setLoading(true);
    fetchServiceEntries({ search, type })
      .then(setEntries)
      .finally(() => setLoading(false));
  }, [search, type]);

  React.useEffect(() => {
    refreshEntries();
  }, [refreshEntries]);

  const handleAdd = async (data: ServiceBookSubmitData) => {
    setLoading(true);
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('Niste ulogovani.');
      await addServiceEntry({ ...data, user_id: user.id });
      setShowModal(false);
      refreshEntries();
    } catch (e) {
      alert('Error adding entry!');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (data: ServiceBookSubmitData) => {
    if (!editEntry) return;
    setLoading(true);
    try {
      await updateServiceEntry(editEntry.id, data);
      setEditEntry(null);
      refreshEntries();
    } catch (e) {
      alert('Error updating entry!');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await deleteServiceEntry(id);
      refreshEntries();
    } catch (e) {
      alert('Error deleting entry!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <input
            type="text"
            placeholder="Search by notes or type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 px-4 py-2 rounded-md border border-theme-primary bg-theme-primary text-theme-primary placeholder-theme-tertiary focus:outline-none"
          />
          <select
            value={type}
            onChange={e => setType(e.target.value)}
            className="px-4 py-2 rounded-md border border-theme-primary bg-theme-primary text-theme-primary focus:outline-none"
          >
            {SERVICE_TYPES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded-md bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors"
          >
            + Add
          </button>
        </div>

        <div className="bg-theme-primary rounded-lg shadow-sm border border-theme-primary p-6 min-h-[200px]">
          {loading ? (
            <div className="text-theme-secondary text-center">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="text-theme-tertiary text-center">No service entries found.</div>
          ) : (
            <div className="grid grid-cols-1">
              {entries.map(entry => {
                const Icon = TYPE_ICONS[entry.type] || HelpCircle;
                return (
                  <ServiceBookEntry
                    key={entry.id}
                    entry={entry}
                    Icon={Icon}
                    onEdit={() => setEditEntry(entry)}
                    onDelete={() => handleDelete(entry.id)}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Add modal */}
        <ServiceBookForm
          open={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleAdd}
        />
        {/* Edit modal */}
        <ServiceBookForm
          open={!!editEntry}
          onClose={() => setEditEntry(null)}
          onSubmit={handleEdit}
          initialData={editEntry || undefined}
        />
      </div>
    </div>
  );
};

export default ServiceBookSection; 